import axios from 'axios';
import { call, put, takeLatest } from 'redux-saga/effects';
import { PayloadAction } from '@reduxjs/toolkit';
import { AUTH_LOGOUT_REASON, AUTH_STORAGE, LogoutReason } from '../constants/auth';
import { authActions, LoginRequestPayload, LogoutRequestPayload, authApi } from './authSlice';

/** Persists authenticated user data after a successful login response. */
function persistAuth(token: string, user: unknown): void {
  localStorage.setItem(AUTH_STORAGE.TOKEN, token);
  localStorage.setItem(AUTH_STORAGE.USER, JSON.stringify(user));
}

/** Removes persisted auth data after logout or session expiry. */
function clearAuth(): void {
  localStorage.removeItem(AUTH_STORAGE.TOKEN);
  localStorage.removeItem(AUTH_STORAGE.USER);
}

/** Handles login API work and resolves/rejects the caller promise used by Redux auth hooks. */
export function* loginWorker(action: PayloadAction<LoginRequestPayload>) {
  try {
    const response: { data: { token: string; user: any } } = yield call(
      axios.post,
      authApi.loginUrl,
      { username: action.payload.username, password: action.payload.password }
    );
    persistAuth(response.data.token, response.data.user);
    yield put(authActions.loginSuccess({ token: response.data.token, user: response.data.user }));
    action.payload.resolve?.();
  } catch (error) {
    yield put(authActions.loginFailure());
    action.payload.reject?.(error);
  }
}

/** Handles logout API work, local session cleanup, and Redux auth state reset. */
export function* logoutWorker(action: PayloadAction<LogoutRequestPayload | undefined>) {
  const reason: LogoutReason = action.payload?.reason ?? AUTH_LOGOUT_REASON.MANUAL;
  const notifyServer = action.payload?.notifyServer ?? reason === AUTH_LOGOUT_REASON.MANUAL;
  const storedToken = localStorage.getItem(AUTH_STORAGE.TOKEN);

  if (storedToken && notifyServer) {
    try {
      yield call(axios.post, authApi.logoutUrl, {}, { headers: { Authorization: `Bearer ${storedToken}` } });
    } catch {
      // Logout should still clear local state if the server call fails.
    }
  }

  clearAuth();
  yield put(authActions.logoutLocal(reason));
}

/** Refreshes server-side activity for a logged-in user without changing visible UI state. */
export function* refreshServerActivityWorker() {
  const storedToken = localStorage.getItem(AUTH_STORAGE.TOKEN);
  if (!storedToken) return;

  try {
    yield call(axios.get, authApi.meUrl, { headers: { Authorization: `Bearer ${storedToken}` } });
  } catch {
    // The global axios interceptor handles auth failures.
  }
}

/** Watches auth-related Redux actions and routes async side effects through sagas. */
export function* authSaga() {
  yield takeLatest(authActions.loginRequest.type, loginWorker);
  yield takeLatest(authActions.logoutRequest.type, logoutWorker);
  yield takeLatest(authActions.refreshServerActivityRequest.type, refreshServerActivityWorker);
}
